import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { decoratorAPI, messageAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';
import './DecoratorDetail.css';

export default function DecoratorDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [decorator, setDecorator]     = useState(null);
  const [reviews, setReviews]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [activeTab, setActiveTab]     = useState('portfolio');
  const [selectedImage, setSelectedImage] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    decoratorAPI.getOne(id)
      .then(({ data }) => {
        setDecorator(data.decorator);
        setReviews(data.reviews || []);
      })
      .catch(() => toast.error('Decorator nahi mila'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleBook = () => {
    if (!user) { toast.error('Pehle login karo!'); navigate('/login'); return; }
    if (user.role !== 'client') { toast.error('Sirf clients book kar sakte hain'); return; }
    navigate(`/book/${id}`);
  };

  const handleChat = async () => {
    if (!user) { toast.error('Pehle login karo!'); navigate('/login'); return; }
    if (user.role === 'decorator') { toast.error('Decorators apne dashboard se chat kar sakte hain'); return; }

    setChatLoading(true);
    try {
      // ✅ FIX: decorator.user object ya string dono handle karo
      const decoratorUserId = decorator?.user?._id || decorator?.user;
      if (!decoratorUserId) { toast.error('Decorator ka account nahi mila'); return; }

      await messageAPI.createConversation({ recipientId: decoratorUserId });
      toast.success('Chat shuru karo! 💬');
      navigate('/chat');
    } catch (err) {
      toast.error('Chat start nahi ho saka');
    } finally {
      setChatLoading(false);
    }
  };

  // ✅ FIX: WhatsApp number sahi jagah se lo
  const getWhatsApp = () => {
    return decorator?.whatsapp || decorator?.user?.phone || null;
  };

  // ✅ FIX: Stars safely banao — rating 0-5 ke beech honi chahiye
  const getStars = (rating) => {
    const r = Math.min(5, Math.max(0, Math.floor(rating || 0)));
    return '★'.repeat(r) + '☆'.repeat(5 - r);
  };

  if (loading) return <div className="page"><div className="spinner" /></div>;
  if (!decorator) return (
    <div className="page">
      <div className="empty-state">
        <h3>Decorator nahi mila</h3>
        <Link to="/decorators" className="btn btn-primary" style={{ marginTop: '16px' }}>← Wapas Jaao</Link>
      </div>
    </div>
  );

  const whatsappNum = getWhatsApp();

  return (
    <div className="detail-page page">
      <SEOHead
        title={`${decorator.businessName} - Best ${(decorator.vendorType || 'decorator').replace(/_/g, ' ')} in ${decorator.city} CG | DecorHub`}
        description={decorator.metaDescription || decorator.description || `Book ${decorator.businessName} in ${decorator.city} on DecorHub.`}
        keywords={decorator.metaKeywords || `${decorator.businessName}, ${decorator.city}, ${(decorator.vendorType || 'decorator').replace(/_/g, ' ')}`}
        canonicalPath={`/vendor/${decorator.slug || decorator._id}`}
        ogImage={decorator.portfolio?.[0]?.image}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: decorator.businessName,
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: decorator.rating || 0,
            reviewCount: decorator.totalReviews || 0
          }
        }}
      />
      <div className="container">

        {/* Back */}
        <Link to="/decorators" className="back-link">← Wapas Jaao</Link>

        {/* Cover */}
        <div className="detail-hero">
          {decorator.portfolio?.[0] && (
            <img src={decorator.portfolio[0].image} alt={decorator.businessName} className="cover-img" />
          )}
          <div className="cover-overlay">
            {decorator.isFeatured && <span className="badge badge-gold">⭐ Featured</span>}
            {decorator.isVerified && <span className="badge badge-green">✓ Verified</span>}
          </div>
        </div>

        <div className="detail-layout">

          {/* ── MAIN INFO ── */}
          <div className="detail-main">
            <div className="detail-header">
              <div>
                <h1>{decorator.businessName}</h1>
                <p className="detail-city">
                  📍 {decorator.city} • {decorator.experience} years experience
                </p>
              </div>
              <div className="rating-big">
                <span className="stars">{getStars(decorator.rating)}</span>
                <span>{decorator.rating > 0 ? `${decorator.rating}/5` : 'No ratings'}</span>
                <span className="text-muted">({decorator.totalReviews || 0} reviews)</span>
              </div>
            </div>

            {decorator.description && (
              <p className="detail-desc">{decorator.description}</p>
            )}

            {/* Services */}
            {decorator.services?.length > 0 && (
              <div className="services-badges">
                {decorator.services.map(s => (
                  <span key={s} className="badge badge-gold">{s}</span>
                ))}
              </div>
            )}

            {/* Tags — ✅ NEW: tags dikhao agar hain */}
            {decorator.tags?.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px' }}>
                {decorator.tags.map(tag => (
                  <span key={tag} style={{ padding: '3px 10px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '100px', fontSize: '12px', color: '#64748b' }}>
                    🏷️ {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Social links — ✅ NEW: instagram/facebook/website dikhao */}
            {(decorator.instagram || decorator.facebook || decorator.website) && (
              <div style={{ display: 'flex', gap: '10px', marginTop: '14px', flexWrap: 'wrap' }}>
                {decorator.instagram && (
                  <a href={decorator.instagram.startsWith('http') ? decorator.instagram : `https://${decorator.instagram}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#e1306c', fontWeight: 600, textDecoration: 'none' }}>
                    📸 Instagram
                  </a>
                )}
                {decorator.facebook && (
                  <a href={decorator.facebook.startsWith('http') ? decorator.facebook : `https://${decorator.facebook}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#1877f2', fontWeight: 600, textDecoration: 'none' }}>
                    👥 Facebook
                  </a>
                )}
                {decorator.website && (
                  <a href={decorator.website.startsWith('http') ? decorator.website : `https://${decorator.website}`} target="_blank" rel="noopener noreferrer"
                    style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: '#0369a1', fontWeight: 600, textDecoration: 'none' }}>
                    🌐 Website
                  </a>
                )}
              </div>
            )}

            {/* Tabs */}
            <div className="tabs" style={{ marginTop: '24px' }}>
              {['portfolio', 'reviews'].map(tab => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab === 'portfolio'
                    ? `📸 Portfolio (${decorator.portfolio?.length || 0})`
                    : `⭐ Reviews (${reviews.length})`}
                </button>
              ))}
            </div>

            {/* Portfolio */}
           {activeTab === 'portfolio' && (
  <div className="portfolio-grid">
    {!decorator.portfolio?.length
      ? <div className="empty-state"><p>Portfolio photos abhi nahi hain</p></div>
      : decorator.portfolio.map(photo => (
        <div key={photo._id} className="portfolio-item"
          onClick={() => !photo.image?.includes('/video/') && setSelectedImage(photo.image)}>
          
          {photo.image?.includes('/video/') ? (
            <video
              src={photo.image}
              controls
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <img src={photo.image} alt={photo.caption || decorator.businessName} loading="lazy" />
          )}

          {photo.caption && <div className="photo-caption">{photo.caption}</div>}
        </div>
      ))
    }
  </div>
)}
            {/* Reviews */}
            {activeTab === 'reviews' && (
              <div className="reviews-list">
                {!reviews.length
                  ? <div className="empty-state"><p>Abhi koi review nahi hai</p></div>
                  : reviews.map(review => (
                    <div key={review._id} className="review-card card" style={{ padding: '20px', marginBottom: '16px' }}>
                      <div className="review-header">
                        <strong>{review.client?.name || 'Anonymous'}</strong>
                        <span className="stars">{getStars(review.rating)}</span>
                      </div>
                      {review.comment && (
                        <p style={{ color: 'var(--text-muted)', marginTop: '8px' }}>{review.comment}</p>
                      )}
                      {review.photos?.length > 0 && (
                        <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                          {review.photos.map((p, i) => (
                            <img
                              key={i} src={p} alt=""
                              style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px', cursor: 'pointer' }}
                              onClick={() => setSelectedImage(p)}
                            />
                          ))}
                        </div>
                      )}
                      <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '8px', display: 'block' }}>
                        {new Date(review.createdAt).toLocaleDateString('hi-IN')}
                      </span>
                    </div>
                  ))
                }
              </div>
            )}
          </div>

          {/* ── SIDEBAR ── */}
          <div className="detail-sidebar">
            <div className="price-card card" style={{ padding: '28px' }}>
              <h3>Price Range</h3>
              <div className="big-price">
                ₹{((decorator.priceRange?.min || 0) / 1000).toFixed(0)}k — ₹{((decorator.priceRange?.max || 0) / 1000).toFixed(0)}k
              </div>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '20px' }}>
                Event type ke hisaab se price vary kar sakta hai
              </p>

              {/* Book Now */}
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={handleBook}
              >
                📅 Book Now
              </button>

              {/* Chat — sirf clients ke liye */}
              {user?.role === 'client' && (
                <button
                  className="btn btn-outline"
                  style={{ width: '100%', justifyContent: 'center', marginTop: '12px' }}
                  onClick={handleChat}
                  disabled={chatLoading}
                >
                  {chatLoading ? '⏳ Opening...' : '💬 Chat Karo'}
                </button>
              )}

              {/* WhatsApp — ✅ FIX: sahi number use ho raha hai */}
              {whatsappNum && (
                <a
                  href={`https://wa.me/91${whatsappNum}?text=Namaste! DecorHub se contact kar raha hoon, decoration ke baare me baat karni thi`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline"
                  style={{ width: '100%', justifyContent: 'center', marginTop: '12px', background: '#ecfdf5', borderColor: '#6ee7b7', color: '#059669', textDecoration: 'none', display: 'flex' }}
                >
                  📱 WhatsApp Karo
                </a>
              )}

              {/* Guest prompt */}
              {!user && (
                <div style={{ marginTop: '16px', background: '#fef3c7', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
                  <p style={{ fontSize: '13px', color: '#92400e', margin: '0 0 8px' }}>
                    Chat ya book karne ke liye login karo
                  </p>
                  <Link to="/login" className="btn btn-primary btn-sm" style={{ width: '100%', justifyContent: 'center' }}>
                    Login Karo
                  </Link>
                </div>
              )}
            </div>

            {/* Quick Info */}
            <div className="card" style={{ padding: '20px', marginTop: '16px' }}>
              <h4>Quick Info</h4>
              <div className="quick-info">
                <div><span>📍 City</span><strong>{decorator.city}</strong></div>
                <div><span>🎊 Events</span><strong>{decorator.services?.length || 0} types</strong></div>
                <div><span>⭐ Rating</span><strong>{decorator.rating || 'New'}</strong></div>
                <div><span>💼 Experience</span><strong>{decorator.experience} years</strong></div>
                <div><span>✅ Bookings</span><strong>{decorator.totalBookings || 0}+</strong></div>
                {/* ✅ NEW: Team size aur response time */}
                {decorator.teamSize && (
                  <div><span>👥 Team</span><strong>{decorator.teamSize}</strong></div>
                )}
                {decorator.responseTime && (
                  <div><span>⚡ Response</span><strong>{decorator.responseTime}</strong></div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox — ✅ FIX: keyboard se bhi band ho */}
      {selectedImage && (
        <div
          className="lightbox"
          onClick={() => setSelectedImage(null)}
          onKeyDown={e => e.key === 'Escape' && setSelectedImage(null)}
          tabIndex={0}
        >
          <img src={selectedImage} alt="Portfolio" onClick={e => e.stopPropagation()} />
          <button className="lightbox-close" onClick={() => setSelectedImage(null)}>✕</button>
        </div>
      )}
    </div>
  );
}